import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DesignService } from '../services/design-service.js';
import { AIService, SPACE_CATEGORIES, EXTERIOR_STYLES, GARDEN_STYLES } from '../services/ai-service.js';
import { QueryCache } from '../services/cache.js';
import { gridImage, fallbackToFull } from '../utils/design-image.js';
import { useLocale } from '../hooks/useLocale.jsx';
import { MyTabs } from '../components/MyTabs.jsx';

// Cache key per filter combo so revisiting "the same page" hits cache.
function userDesignsCacheKey(uid, { favoritesOnly, spaceCategory, styleFilter }) {
  return `userDesigns:${uid}:${favoritesOnly ? 'fav' : 'all'}:${spaceCategory || '*'}:${styleFilter || '*'}`;
}

export function MyDesigns({ user }) {
  const navigate = useNavigate();
  const { t, styleName } = useLocale();
  const isLoggedIn = user && !user.isAnonymous;

  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [spaceCategory, setSpaceCategory] = useState(null);
  const [styleFilter, setStyleFilter] = useState(null);

  // Synchronously hydrate from cache if present so navigation back to
  // /designs renders the previously-seen grid instantly. Filter combo is
  // baked into the cache key — switching filters falls back to fresh fetch.
  const initialCache = isLoggedIn
    ? QueryCache.get(userDesignsCacheKey(user.uid, { favoritesOnly, spaceCategory, styleFilter }))
    : null;
  const [designs, setDesigns] = useState(initialCache?.designs || []);
  const [loading, setLoading] = useState(!initialCache);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);

  const loadDesigns = useCallback(async (reset = false) => {
    if (!isLoggedIn) return;
    const key = userDesignsCacheKey(user.uid, { favoritesOnly, spaceCategory, styleFilter });

    if (reset) {
      // Peek cache for the current filter combo. Hit → render instantly + still
      // fire the fetch to refresh in background. Miss → only show spinner if
      // we don't have any designs to keep visible.
      const cached = QueryCache.get(key);
      if (cached) {
        setDesigns(cached.designs);
        setLoading(false);
      } else if (designs.length === 0) {
        setLoading(true);
      }
    } else {
      setLoadingMore(true);
    }

    try {
      const { designs: fetched, lastVisible, hasMore: more } = await DesignService.getUserDesigns(
        user.uid,
        {
          lastDoc: reset ? null : lastDoc,
          favoritesOnly,
          spaceCategory,
          styleFilter,
        }
      );
      setDesigns(prev => reset ? fetched : [...prev, ...fetched]);
      setLastDoc(lastVisible);
      setHasMore(more);
      if (reset) QueryCache.set(key, { designs: fetched });
    } catch (err) {
      setError(t('errLoadDesigns'));
      console.error(err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [isLoggedIn, user?.uid, lastDoc, favoritesOnly, spaceCategory, styleFilter, t, designs.length]);

  useEffect(() => {
    loadDesigns(true);
  }, [user, favoritesOnly, spaceCategory, styleFilter]); // eslint-disable-line

  const toggleFilter = () => {
    setFavoritesOnly(prev => !prev);
    setLastDoc(null);
  };

  const handleSpaceCategoryChange = (cat) => {
    setSpaceCategory(cat);
    setStyleFilter(null);
    setLastDoc(null);
  };

  // Style catalog adapts to selected category — same pattern as CommunityFeed.
  const styleCatalogForFilter = spaceCategory === 'exterior'
    ? EXTERIOR_STYLES
    : spaceCategory === 'garden'
      ? GARDEN_STYLES
      : AIService.interiorStyles;
  const allStyles = Object.keys(styleCatalogForFilter).filter(k => k !== 'custom');

  if (!isLoggedIn) {
    return (
      <main className="main">
        <MyTabs user={user} current="designs" />
        <div style={{ textAlign: 'center', paddingTop: '3rem' }}>
          <i className="material-symbols-rounded" style={{ fontSize: '3rem', color: 'var(--text-muted)' }}>lock</i>
          <h2 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>{t('signInToSee')}</h2>
          <p style={{ color: 'var(--text-muted)' }}>{t('signInToSeeDesc')}</p>
        </div>
      </main>
    );
  }

  return (
    <main className="main">
      {/* 모바일 한정 sticky+auto-hide wrapper. 데스크톱은 inline 그대로.
         MyTabs + 카테고리/스타일 필터 + 즐겨찾기 토글까지 한 묶음. */}
      <div className="feed-sticky-mobile">
      <MyTabs user={user} current="designs" />

      {/* Category filter + favorites toggle 한 줄. heart 는 우측 끝, 카테고리 chip 들은 가로 스크롤. */}
      <div className="my-designs-filter-row">
        <div className="feed-category-filter">
          <button
            className={`feed-filter-btn ${!spaceCategory ? 'active' : ''}`}
            onClick={() => handleSpaceCategoryChange(null)}
          >
            {t('feedFilterAll')}
          </button>
          {SPACE_CATEGORIES.map(cat => (
            <button
              key={cat}
              className={`feed-filter-btn ${spaceCategory === cat ? 'active' : ''}`}
              onClick={() => handleSpaceCategoryChange(cat)}
            >
              {t('spaceCategories.' + cat)}
            </button>
          ))}
        </div>
        <button
          className={`detail-icon-btn my-designs-favorite-toggle ${favoritesOnly ? 'fav-active' : ''}`}
          onClick={toggleFilter}
          title={favoritesOnly ? t('myDesignsTitle') : t('noFavorites')}
        >
          <i className="material-symbols-rounded">favorite</i>
        </button>
      </div>

      {/* Style filter — only when a specific category is selected */}
      {spaceCategory && (
        <div className="feed-style-filter">
          <button className={`feed-filter-btn ${!styleFilter ? 'active' : ''}`} onClick={() => setStyleFilter(null)}>
            {t('feedFilterAll')}
          </button>
          {allStyles.map(key => (
            <button
              key={key}
              className={`feed-filter-btn ${styleFilter === key ? 'active' : ''}`}
              onClick={() => setStyleFilter(key)}
            >
              {styleName(key, spaceCategory)}
            </button>
          ))}
          {spaceCategory === 'interior' && (
            <button
              className={`feed-filter-btn ${styleFilter === 'custom' ? 'active' : ''}`}
              onClick={() => setStyleFilter('custom')}
            >
              {styleName('custom')}
            </button>
          )}
        </div>
      )}
      </div>{/* /.feed-sticky-mobile */}

      {loading ? (
        <div className="loading"><div className="spinner"></div><p>{t('loadingDesigns')}</p></div>
      ) : error ? (
        <div className="error-message" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
          <span>{error}</span>
          <button className="btn btn-secondary" style={{ flexShrink: 0, fontSize: '0.8125rem' }} onClick={() => { setError(null); loadDesigns(true); }}>
            {t('retry')}
          </button>
        </div>
      ) : designs.length === 0 ? (
        <div style={{ textAlign: 'center', paddingTop: '3rem' }}>
          <i className="material-symbols-rounded" style={{ fontSize: '3rem', color: 'var(--text-muted)' }}>
            {favoritesOnly ? 'favorite' : 'photo_library'}
          </i>
          <p style={{ marginTop: '1rem', color: 'var(--text-muted)' }}>
            {favoritesOnly ? t('noFavorites') : t('noDesigns')}
          </p>
          {!favoritesOnly && !spaceCategory && !styleFilter && (
            <button className="btn btn-primary" style={{ marginTop: '1.5rem' }} onClick={() => navigate(window.matchMedia('(max-width: 768px)').matches ? '/create' : '/')}>
              {t('createDesign')}
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="design-grid">
            {designs.map(design => (
              <DesignCard
                key={design.id}
                design={design}
                onClick={() => navigate(`/designs/${design.id}`)}
              />
            ))}
          </div>
          {hasMore && (
            <div className="controls">
              <button
                className="btn btn-secondary"
                onClick={() => loadDesigns(false)}
                disabled={loadingMore}
              >
                {loadingMore ? t('loadingDesigns') : t('loadMore')}
              </button>
            </div>
          )}
        </>
      )}
    </main>
  );
}

function DesignCard({ design, onClick }) {
  const { styleName, lang } = useLocale();
  const thumb = gridImage(design) || design.originalImageUrls?.[0];
  const styleLabel = styleName(design.style, design.spaceCategory);

  return (
    <div className="design-card" onClick={onClick}>
      <div className="design-card-thumb">
        {thumb ? (
          <img
            src={thumb}
            alt={styleLabel}
            loading="lazy"
            onError={fallbackToFull(design)}
            ref={(el) => {
              if (el && el.complete && el.naturalWidth > 0) {
                el.classList.add('is-loaded');
              }
            }}
            onLoad={(e) => e.currentTarget.classList.add('is-loaded')}
          />
        ) : (
          <div className="design-card-no-thumb">
            <i className="material-symbols-rounded">image</i>
          </div>
        )}
        {design.isFavorite && (
          <span className="design-card-fav"><i className="material-symbols-rounded">favorite</i></span>
        )}
      </div>
      <div className="design-card-info">
        <span className="design-card-style">{styleLabel}</span>
        {design.name && <p className="design-card-name">{design.name}</p>}
        <span className="design-card-date">{DesignService.formatDate(design.timestamp, lang)}</span>
        {design.photoCount > 1 && (
          <span className="design-card-photos">
            <i className="material-symbols-rounded" style={{ fontSize: '0.875rem' }}>photo_library</i>
            {design.photoCount} photos
          </span>
        )}
      </div>
    </div>
  );
}
